import {
  VENDOR_IMPORT_INDIVIDUAL_REASON_MAX,
  VENDOR_IMPORT_MAX_COLUMNS,
  VENDOR_IMPORT_MAX_ROWS,
  type CloudeqEntity,
  type VendorAddressField,
  type VendorImportAction,
  type VendorImportAliasColumn,
  type VendorImportAliasStatus,
  type VendorImportConflictReason,
  type VendorImportEntitySource,
  type VendorImportFileRefusal,
  type VendorImportFlag,
  type VendorImportIndividualTestColumn,
  type VendorImportLinkMatch,
  type VendorImportPlanTotals,
  type VendorImportReason,
  type VendorImportRowOutcome,
  type VendorImportSheetFlag,
  type VendorImportSheetState,
  type VendorImportSourceSystem,
  type VendorImportUnprocessableReason,
  type VendorSheetCheckField,
  type VendorSheetField,
} from '@clm/shared-types';

import type { RowGap } from './vendor-import/decisions';

/**
 * Every sentence the **Vendor list** surface shows — CLM-113 + CLM-114.
 *
 * One `Record<…, string>` per wire vocabulary, keyed by the vocabulary's own
 * type, so a member added to `@clm/shared-types` with no copy here is a compile
 * error (§4.7: "a vocabulary member with no copy is a compile error"). The
 * page never prints a raw token: a reason, flag, alias status or refusal the
 * server sends is always read through one of these tables.
 *
 * Nothing here names an individual, a PAN or a contact detail; the page is never
 * sent one to name (R-113-4, R-113-5, R-113-26).
 */

/** The plan's per-row reasons (§3.4): skip reasons, then reject reasons. */
export const REASON_COPY: Readonly<Record<VendorImportReason, string>> = {
  unchanged: 'Unchanged — CLM already holds exactly what this row says, so nothing is written.',
  empty_record: 'An empty Business Central record: a vendor number and nothing else. Not imported.',
  individual: 'An individual. Personal data is not imported unless you include the person with a reason.',
  decision_required: 'Needs your decision. Nothing is written for this row until you choose.',
  privacy_blocked: 'Privacy Blocked in the source. Refused, and it cannot be included.',
  missing_vendor_number: 'No vendor number, so the row cannot be identified. Fix the card in Business Central.',
  duplicate_vendor_number: 'This vendor number appears more than once in the sheet. Every occurrence is refused.',
  missing_name: 'No Name. A vendor is imported by its Name; fix the card in Business Central.',
  unusable_name: 'The Name has no letter or digit CLM can match on.',
  value_too_long: 'A cell is longer than CLM stores for its column.',
  cell_error: 'A cell holds a spreadsheet error or a control character.',
  cloudeq_entity: 'This is a cloudEQ company. cloudEQ is never a counterparty party.',
};

/** The plan's informational row flags. Shown as text and an icon, never colour alone. */
export const FLAG_COPY: Readonly<Record<VendorImportFlag, string>> = {
  privacy_blocked_after_import:
    'CLM already holds this vendor, and the source now blocks it for privacy. Only a person can act on what CLM holds.',
  now_individual_in_sheet:
    'Imported earlier as an organisation; the sheet now marks it as an individual. Approving clears its address. Names already imported stay — retire them on Party aliases if they should go.',
  name_changed:
    'The Name is new to this vendor: the same vendor renamed, or a vendor number re-used for another supplier? Nothing is assumed.',
  display_name_changed_in_clm: 'Someone renamed this vendor in CLM, so its display name no longer follows the sheet.',
  alias_retired_in_clm:
    'A name on this row was retired or rejected in CLM. The import never brings it back — not as a name, and not as the display name.',
  party_inactive: 'The vendor is inactive in CLM. An import updates it but never reactivates it.',
  short_name_confirmation_required:
    'A name under five characters. A short name can substitute into ordinary text, so it needs your explicit confirmation.',
  alias_shared_with_other_party:
    'Another party already holds a name on this row, or an earlier row of this sheet would give it one. Kept on both, the name no longer picks a party at intake; a person chooses.',
  alias_pending_on_other_party:
    'A name on this row is pending as a proposal on a party this row is not joined to. The import never confirms it; it stays in the party-alias queue.',
  alias_pending_will_confirm: 'A pending proposal on this vendor with this name is confirmed by this import.',
  vendor_no_known_under_other_entity:
    'This vendor number is already imported under another cloudEQ company. Check that this sheet is the company you think it is.',
  search_name_truncation_ignored: 'The Search Name is a shortened copy of the Name, so it is not imported as a name.',
  single_word_alias: 'A one-word name. It will match file and folder names at bulk intake.',
  name_may_be_truncated: 'The Name is exactly as long as Business Central allows; it may have been cut short.',
  country_code_normalised: 'The country code was not an ISO code and was read as one (for example UK as GB).',
  country_code_unrecognised: 'The country code matches no country. It is left out; the vendor is still imported.',
  same_pan_as_earlier_row:
    'An earlier row of this sheet has the same PAN, so this may be the same legal entity. The PAN itself is never shown or kept.',
};

/** Flags that ask something of the Admin read `⚠`; the rest are information (`ℹ`). */
export const FLAG_NEEDS_ATTENTION: Readonly<Record<VendorImportFlag, boolean>> = {
  privacy_blocked_after_import: true,
  now_individual_in_sheet: true,
  name_changed: true,
  display_name_changed_in_clm: false,
  alias_retired_in_clm: false,
  party_inactive: true,
  short_name_confirmation_required: true,
  alias_shared_with_other_party: true,
  alias_pending_on_other_party: true,
  alias_pending_will_confirm: false,
  vendor_no_known_under_other_entity: true,
  search_name_truncation_ignored: false,
  single_word_alias: true,
  name_may_be_truncated: true,
  country_code_normalised: false,
  country_code_unrecognised: false,
  same_pan_as_earlier_row: true,
};

export const SHEET_FLAG_COPY: Readonly<Record<VendorImportSheetFlag, string>> = {
  individual_test_weaker:
    'This sheet lacks a column an earlier import for this company used to recognise individuals (Assessee Code, P.A.N. No. or Partner Type), so rows that are people may now read as organisations, addresses included.',
};

/** An imported name's status, on its chip. */
export const ALIAS_STATUS_COPY: Readonly<Record<VendorImportAliasStatus, string>> = {
  new: 'new',
  exists: 'already held',
  exists_retired: 'retired in CLM — not brought back',
  omitted: 'left out earlier — not offered again',
  pending_will_confirm: 'pending — confirmed by this import',
  shared_with_other_party: 'another party holds this name',
};

export const ALIAS_COLUMN_COPY: Readonly<Record<VendorImportAliasColumn, string>> = {
  name: 'Name',
  name_2: 'Name 2',
  search_name: 'Search Name',
};

/**
 * File refusals — the leading token of `field_errors[0].message` on a `400`
 * (R-113-21). Each sentence names the reason and the fix.
 */
export const FILE_REFUSAL_COPY: Readonly<Record<VendorImportFileRefusal, string>> = {
  ole_container:
    'This is a legacy .xls workbook or a password-protected one. Remove the password, or open it in Excel and save as .xlsx.',
  macro_enabled_workbook: 'This workbook carries macros. Save it as an Excel Workbook (.xlsx) and choose it again.',
  not_a_workbook: 'This is a ZIP file but not an Excel workbook. Export the vendor list as .xlsx or as CSV UTF-8.',
  not_utf8_csv: 'This CSV is not UTF-8 text. In Excel, save as “CSV UTF-8 (Comma delimited)”.',
  csv_malformed: 'This CSV has a quote that never closes. Re-save it from Excel as “CSV UTF-8 (Comma delimited)”.',
  media_type_mismatch:
    'The contents do not match the extension (an .xlsx renamed .csv, or the reverse). Give the file its right extension.',
  too_many_entries: 'The workbook has more parts than a vendor list needs. Export the list again from Business Central.',
  unsafe_entry_path: 'A part of the workbook has a path that escapes the workbook, so it was refused as unsafe. Export the list again.',
  encrypted_entry: 'Part of the workbook is encrypted. Remove the password and save as .xlsx.',
  undecodable_entry: 'Part of the workbook could not be read. Open it in Excel, save as .xlsx and try again.',
  entry_too_large: 'One part of the workbook is larger than a vendor list can be. Export only the vendor list.',
  total_too_large: 'The workbook unpacks to more than a vendor list can be. Export only the vendor list.',
  inflate_ratio: 'Part of the workbook compresses far beyond what a spreadsheet does, so it was refused as unsafe.',
  duplicate_part:
    'The workbook has two parts with the same name, so Excel and CLM could read different contents. Open it in Excel and save as .xlsx.',
  xml_doctype_refused:
    'The workbook declares a document type or entities, which a spreadsheet never needs, so it was refused as unsafe.',
  xml_undefined_entity: 'The workbook refers to an entity it never defines. Open it in Excel and save as .xlsx.',
  xml_malformed: 'The workbook’s XML is not well formed. Open it in Excel and save as .xlsx.',
  no_sheet: 'The first sheet of the workbook is a chart. Move the vendor list to the first sheet.',
  header_not_found:
    'No header row with a vendor-number column and a name column was found in the first 20 rows. A CSV separated by semicolons or tabs reads this way: save as “CSV UTF-8 (Comma delimited)”.',
  duplicate_column: 'Two columns map to the same field (for example “Name” and “Vendor Name”). Delete one and choose the file again.',
  too_many_rows: `The sheet has more than ${VENDOR_IMPORT_MAX_ROWS.toLocaleString('en-GB')} data rows, more than one import takes. Split it.`,
  too_many_columns: `The sheet has more than ${String(VENDOR_IMPORT_MAX_COLUMNS)} columns. Delete the columns the import does not need.`,
  too_many_strings: 'The workbook holds more distinct text values than one import reads. Split it.',
};

/** `422` reasons, at the request path they name. Nothing has been written. */
export const UNPROCESSABLE_COPY: Readonly<Record<VendorImportUnprocessableReason, string>> = {
  entity_unknown: 'That cloudEQ company is not an active entity. Choose another.',
  entity_contradicts_sheet: 'The sheet’s heading names a different cloudEQ company, and a company read from the sheet cannot be changed.',
  individual_test_weaker_unconfirmed: 'Confirm that this sheet has been checked for individuals before importing.',
  row_not_in_plan: 'This row is not in the plan the API recomputed. Read the sheet again.',
  action_not_offered: 'That choice is not offered for this row.',
  link_target_not_offered:
    'This row can join only a vendor it was matched with, or an earlier row imported as a new vendor in this import.',
  short_name_unconfirmed: 'Confirm the short name before this row is imported.',
  shared_alias_unconfirmed: 'This row adds a name another party holds. Keep it on both parties, or leave that name out.',
  individual_reason_required: 'Including a person needs a reason.',
  individual_reason_not_applicable: 'A reason is given only to include an individual.',
  omit_not_offered: 'That name cannot be left out on this row.',
};

/**
 * `409` reasons on execute. The page has already read the sheet again when it
 * shows one, and says that every decision was cleared (§4.7).
 */
export const CONFLICT_COPY: Readonly<Record<VendorImportConflictReason, string>> = {
  file_changed: 'The file changed after it was read.',
  plan_changed:
    'The vendor registry changed after the sheet was read: a vendor was registered, a party alias decided, or another import ran.',
  registry_changed: 'The party registry changed while this import ran.',
};

export const CONFLICT_FOLLOW_UP =
  'The sheet has been read again and every decision has been cleared. Review the rows and make your choices again.';

export const LINK_MATCH_COPY: Readonly<Record<VendorImportLinkMatch, string>> = {
  display_name: 'same name',
  alias: 'same name as one of its party aliases',
  sheet_row: 'same name as an earlier row',
  sheet_row_pan: 'same PAN as an earlier row',
};

export const FIELD_LABEL: Readonly<Record<VendorSheetField, string>> = {
  vendor_no: 'Vendor no.',
  name: 'Name',
  name_2: 'Name 2',
  search_name: 'Search Name',
  address_line_1: 'Address',
  address_line_2: 'Address 2',
  city: 'City',
  post_code: 'Post code',
  county: 'County',
  state_code: 'State code',
  country_code: 'Country',
};

/** The four check-only columns, and what each decides (R-113-4). Never a value. */
export const CHECK_FIELD_COPY: Readonly<Record<VendorSheetCheckField, string>> = {
  assessee_code: 'decides whether a row is an individual (IND or HUF)',
  partner_type: 'decides whether a row is an individual (Person)',
  pan: 'decides whether a row is an individual (its fourth letter) and whether two rows are one company; never stored or shown',
  privacy_blocked: 'refuses a vendor the source blocks for privacy',
};

export const ADDRESS_FIELD_LABEL: Readonly<Record<VendorAddressField, string>> = {
  address_line_1: 'address',
  address_line_2: 'address line 2',
  city: 'city',
  post_code: 'post code',
  county: 'county',
  state_code: 'state code',
  country_code: 'country',
};

export const OUTCOME_COPY: Readonly<Record<VendorImportRowOutcome, string>> = {
  created: 'created',
  linked: 'linked',
  updated: 'updated',
  unchanged: 'unchanged',
  skipped: 'skipped',
  rejected: 'rejected',
};

export const SOURCE_SYSTEM_COPY: Readonly<Record<VendorImportSourceSystem, string>> = {
  dynamics_bc: 'Read as a Business Central vendor export.',
  vendor_sheet: 'Read as a vendor sheet.',
};

export const SHEET_STATE_COPY: Readonly<Record<VendorImportSheetState, string>> = {
  visible: 'The first sheet is visible.',
  hidden: 'The first sheet is hidden in Excel; it was read anyway.',
  very_hidden: 'The first sheet is very hidden in Excel; it was read anyway.',
};

export const ENTITY_SOURCE_COPY: Readonly<Record<VendorImportEntitySource, string>> = {
  sheet_preamble: 'from the sheet’s heading',
  admin: 'chosen by an admin',
};

export const INDIVIDUAL_TEST_COLUMN_COPY: Readonly<Record<VendorImportIndividualTestColumn, string>> = {
  assessee_code: 'Assessee Code',
  pan: 'P.A.N. No.',
  partner_type: 'Partner Type',
};

export const ACTION_COPY: Readonly<Record<VendorImportAction, string>> = {
  create: 'Create',
  update: 'Update',
  link: 'Link',
  skip: 'Skip',
  reject: 'Reject',
};

export type VendorImportTile = Exclude<keyof VendorImportPlanTotals, 'data_rows' | 'not_in_sheet'>;

/** The summary tiles and the filter, in display order. */
export const TILE_COPY: Readonly<Record<VendorImportTile, string>> = {
  create: 'Create',
  update: 'Update',
  link: 'Link',
  unchanged: 'Unchanged',
  decision_required: 'Needs your decision',
  individual: 'Not imported: personal data',
  empty_record: 'Empty records',
  rejected: 'Rejected',
};

export const TILE_ORDER: readonly VendorImportTile[] = [
  'create',
  'update',
  'link',
  'unchanged',
  'decision_required',
  'individual',
  'empty_record',
  'rejected',
];

/** Extensions refused in the browser, before anything is sent (§4.7). */
export type ExtensionRefusal = 'xls' | 'xlsm' | 'xlsb' | 'ods' | 'other';

export const EXTENSION_REFUSAL_COPY: Readonly<Record<ExtensionRefusal, string>> = {
  xls: 'A legacy .xls workbook is not read. Open it in Excel and save as .xlsx.',
  xlsm: 'A macro-enabled workbook (.xlsm) is not read. Save it as an Excel Workbook (.xlsx).',
  xlsb: 'A binary workbook (.xlsb) is not read. Save it as an Excel Workbook (.xlsx).',
  ods: 'An OpenDocument spreadsheet (.ods) is not read. Save it as .xlsx, or as CSV UTF-8.',
  other: 'Only an Excel workbook (.xlsx) or a CSV file (.csv) is read.',
};

/** What a ticked row still needs before the Import button is enabled. */
export const GAP_COPY: Readonly<Record<RowGap, string>> = {
  choose: 'Choose what this row is.',
  reason: 'Give a reason for including this person.',
  reason_too_long: `Keep the reason to ${String(VENDOR_IMPORT_INDIVIDUAL_REASON_MAX)} characters.`,
  short_names: 'Confirm the short name.',
  shared_names: 'Keep the name on both parties, or leave it out.',
  earlier_row_not_created: 'The earlier row this joins must itself be imported as a new vendor.',
};

/** Why individuals are left out, beside "Include this person" (R-113-5). */
export const INDIVIDUAL_DEFAULT_COPY =
  'People are left out by default: their names are personal data, and a vendor list needs only the companies a contract can name. Include one only when a contract with this person exists or is expected.';

/** What including one means (R-113-5, A7). */
export const INDIVIDUAL_INCLUDE_COPY =
  'Only the person’s name is kept — no address and no other name. Until their first contract, that name is offered in the party picker to everyone who can add a contract.';

/** Beside a Vendor candidate that holds a pending proposal of a name on the row (R-114-2). */
export const CANDIDATE_CONFIRMS_PENDING_COPY =
  'joining this vendor confirms its pending proposal of a name on this row; a new vendor leaves that proposal pending in the party-alias queue.';

/** What keeping a name on two parties does at intake and to FR-29 (R-114-7). */
export const SHARED_ALIAS_COPY =
  'Kept on both parties, the name no longer picks a party at intake — a person chooses — and a contract filed against both parties stops at pseudonymisation instead of substituting the wrong one.';

/** The sentence beside "Leave out this alias" (R-113-14). */
export const OMIT_ALIAS_COPY = 'It will not be offered again; add it later on Party aliases.';

/** The R-114-6 outcome when no existing party with a contract changed its names. */
export const FR29_NO_CHANGE_COPY = 'No existing contract’s FR-29 map changes.';

/** An entity's display name from the registry, or its key while the registry has not loaded. */
export function entityName(entities: readonly CloudeqEntity[], key: string | null): string {
  if (key === null) return '—';
  return entities.find((entity) => entity.entity_key === key)?.display_name ?? key;
}

/** `row:7` → "row 7"; `line:7` → "line 7". */
export function rowLabel(rowRef: string): string {
  return rowRef.replace(':', ' ');
}

/** A byte bound in MB, to two places at most: 786432 → "0.75 MB", 5242880 → "5 MB". */
export function megabytes(bytes: number): string {
  const value = Math.round((bytes / 1_048_576) * 100) / 100;
  return `${value.toLocaleString('en-GB', { maximumFractionDigits: 2 })} MB`;
}

/** The leading token of a field error's message — `"<token>: <detail>"`. */
export function leadingToken(message: string): string {
  const index = message.indexOf(':');
  return (index === -1 ? message : message.slice(0, index)).trim();
}

/** The text after the leading token, or `null` when there is none. */
export function tokenDetail(message: string): string | null {
  const index = message.indexOf(':');
  if (index === -1) return null;
  const detail = message.slice(index + 1).trim();
  return detail === '' ? null : detail;
}

export function isKeyOf<T extends string>(table: Readonly<Record<T, string>>, token: string): token is T {
  return Object.prototype.hasOwnProperty.call(table, token);
}
